<template>
    <div
        ref="rootNode"
        class="expand-horizontal viewer-root"
    >
        <div v-if="timeline == null">
            <b-spinner style="display: block;margin: auto;margin-top: 25vh;" />
        </div>
        <div
            v-else
            class="expand-horizontal"
            style="height: 100%;"
        >
            <div
                v-if="!fullscreen"
                class="expand-horizontal center-text noselect"
                :class="{ 'has-project-watermark': watermarkImage }"
                style="height: auto; min-height: 90px; max-height: 98px; display: flex; justify-content: flex-start; align-items: center; background-color: #FBF9F7; padding: 0px;"
            >
                <component
                    :is="watermarkLink ? 'a' : 'span'"
                    v-if="watermarkImage"
                    class="project-watermark"
                    :href="watermarkLink || undefined"
                    :target="watermarkLink ? '_blank' : undefined"
                    :rel="watermarkLink ? 'noopener noreferrer' : undefined"
                >
                    <img :src="watermarkImage" alt="Project watermark">
                </component>
                <div style="width: 20px; margin-left: 5px; margin-right: 10px;">
                    <b-btn
                        id="descriptionButton"
                        ref="descriptionBtn" 
                        v-b-tooltip="checkTimelineWrite() ? 'Edit Description' : 'View Description'"
                        class="icon-button purple"
                        aria-label="timeline description"
                        @click.stop="showDescriptionPopover = !showDescriptionPopover"
                        @mouseleave="customBlur($refs.descriptionBtn)"
                    >
                        <img
                            src="@/assets/icons/information_solid_img.png"
                            aria-hidden="true"
                            style="width: 20px; height: auto; position: absolute; top: 5px; left: 5px;"
                        >
                    </b-btn>
                    <b-modal
                        v-model="showDescriptionPopover"
                        title="Description"
                        size="lg"
                        ok-only
                        ok-title="Close"
                        ok-variant="secondary"
                    >
                        <!-- description editor -->
                        <p v-if="!checkTimelineWrite()">
                            {{ (this.timeline.description == null || this.timeline.description === '') ? 'No description.' : this.timeline.description }}
                        </p>
                        <b-form-textarea-limited
                            v-else
                            v-model="editDescription"
                            :maxlength="1500"
                            style="min-width: 375px !important;"
                            autofocus
                        />
                    </b-modal>
                    <b-btn
                        id="commentsButton"
                        ref="commentsBtn"
                        v-b-tooltip="'Comments'"
                        class="icon-button purple"
                        aria-label="timeline comments"
                        @click.stop="showCommentsPopover = !showCommentsPopover"
                        @mouseleave="customBlur($refs.commentsBtn)"
                    >
                        <img
                            src="@/assets/icons/chat.png"
                            aria-hidden="true"
                            style="width: 18px; height: auto; position: absolute; top: 6px; left: 6px;"
                        >
                    </b-btn>
                    <b-modal
                        v-model="showCommentsPopover"
                        :title="checkTimelineWrite() ? 'Comments (Project Admins can comment)' : 'Comments'"
                        size="lg"
                        ok-only
                        ok-title="Close"
                        ok-variant="secondary"
                    >
                        <!-- comments editor -->
                        <p v-if="!checkProjectModerate()">
                            {{ (this.timeline.comments == null || this.timeline.comments === '') ? 'No comments.' : this.timeline.comments }}
                        </p>
                        <b-form-textarea-limited
                            v-else
                            v-model="editComments"
                            :maxlength="1500"
                            style="min-width: 375px !important;"
                            autofocus
                        />
                    </b-modal>
                </div>
                <div class="timelineTitleWidth">
                    <div style="max-height: 50px;">
                        <b-btn
                            v-if="checkTimelineAdmin()"
                            ref="editNameBtn"
                            v-b-tooltip="'Edit Name'"
                            class="icon-button purple" 
                            style="margin-right: 7px;"
                            aria-label="edit timeline name"
                            @click.stop="editingName = !editingName"
                            @mouseleave="customBlur($refs.editNameBtn)"
                        >
                            <img
                                src="@/assets/icons/pencil-edit-button.png"
                                style="width: 18px; height: auto; position: absolute; top: 6px; left: 6px;"
                                aria-hidden="true"
                            >
                        </b-btn>
                        <best-fit-text
                            v-if="!editingName"
                            role="heading"
                            aria-level="1"
                            aria-label="timeline name"
                            style="font-weight: bold;"
                        >
                            {{ this.timelineName }}
                        </best-fit-text>
                        <!-- name editor -->
                        <b-form
                            v-else
                            inline
                            style="display: inline-block; margin: 0 30px 0 0; vertical-align: top;"
                        >
                            <b-input
                                v-model="editName"
                                :maxlength="50"
                                placeholder="Enter a name here"
                                :state="editName == '' ? false : null"
                                @submit.prevent
                                @keydown.enter="editingName = false"
                            />
                            <!-- can't use b-input-limited here because I can't figure out how to pass v-on stuff to the grandchild -->
                            <div
                                style="color: #4d535e; font-size: 13px;"
                                :aria-label="`${maxlength} characters allowed, ${50 - editName.length} characters remaining`"
                            >
                                <div aria-hidden="true">
                                    {{ `${editName.length} / 50` }}
                                </div>
                            </div>
                        </b-form>
                        
                        <!--
                        <b-btn v-b-tooltip.hover="'Fullscreen'" @click.stop="fullscreen = true" class="icon-button purple">
                            <img src="@/assets/icons/full-screen-selector.png" alt="Full Screen"
                                style="width: 18px; height: auto; position: absolute; top: 6px; left: 6px;"/>
                        </b-btn>
                        -->
                    </div>
                    <div
                        class="non-mobile-portrait"
                        style="margin-top: 3px; display: flex; text-align: left; justify-content: center; align-items: flex-start;"
                    >
                        <div style="margin: 1px 10px; font-weight: 500">
                            Total Events: {{ timelineevents.length }}
                        </div>
                        <div
                            v-if="checkTimelineWrite() && !user.isSubscribed && !user.trialing && $store.state.project.project.subscriptionTier < 1"
                            style="font-weight: 500; margin: 1px 0 0 -4px"
                        >
                            of 20
                            <b-btn
                                v-b-tooltip="'increase Event limit'"
                                pill
                                style="display: inline-flex; align-items: center; justify-content: center; margin: -2px 0 0 3px; padding: 0; border-radius: 50%; width: 24px; height: 24px; box-shadow: .5px .5px .5px rgba(0, 0, 0, .75);"
                                aria-label="increase event limit"
                                variant="cliovis-blue-light"
                                @click="$refs.subscriptionModal.show()"
                            >
                                <i
                                    class="fas fa-plus"
                                    style="font-size: 15px; margin-left: 5px;"
                                />
                            </b-btn>
                        </div>
                        <div style="margin: 1px 0 0 20px; font-weight: 500">
                            Total Connections: {{ timelineconnections.length }}
                        </div>
                    </div>
                    <div style="margin-top: 1px; display: flex; text-align: left; justify-content: center; align-items: flex-start;">
                        <!-- Hovered node
                        <div style="color: #090909; margin: 0 10px 3px; font-size: 0.95em;">{{drag.hoveredNode ? drag.hoveredNode.name : 'No hovered node'}}</div>
                        -->
                        <div
                            v-if="!checkTimelineWrite()"
                            style="background-color: #7c6231; border-radius: 20px; color: #fff; margin: 0 10px 3px 0; font-size: 1em; font-weight: 500; padding: 0 10px 0 10px"
                        >
                            view only
                        </div>
                    </div>
                </div>
                <div
                    class="tagUI"
                    style="border: none; border-color: #777777; border-radius: 50%; height: 88px; margin: 0px 5px 5px;"
                >
                    <div
                        role="heading"
                        aria-level="2"
                        aria-label="event categories"
                        style="position: absolute; width: 100%; height: 88px; pointer-events: none"
                    />
                    <tag-ui
                        :timeline="timeline"
                        @regen-layout="refreshLayout"
                    />
                </div>
            </div>
            <!--{{trackHeight}} [|||] {{rootBounds.height}} [|||] {{tracks.length}}-->
            <!--tabindex="0"-->
            <!-- style="position: absolute !important; top: 0px; bottom: 0px; left: 0px; right: 0px;background-color: #E7E3E0;" -->

            <quicksearch 
                v-if="isSearching" 
                class="quicksearch" 
                :nodes="timelinenodes" 
                style="top: 8%; z-index: 100000;"
                @close="()=>{isSearching = false;}"
            />
            <full-event-slide 
                v-if="$store.state.project.presentationMode && $store.state.project.presentationFullSlide >= 0" 
                :style="fullscreen ? 'top: 1%; min-height: 82.5%;' : 'top: 7.5%; min-height: 80%;'"
            />

            <div 
                v-if="timelineFormMode"
                id="timelineFormRoot"
                :tabindex="0" 
                role="heading" 
                aria-level="3" 
                :aria-label="`Viewing Timeline Form: ${timelineName} (this form may be easier for screen readers to navigate)`" 
            />

            <timeline-form 
                v-if="timelineFormMode" 
                :fullscreen="fullscreen"
            />
            <div 
                v-else
                id="timelineRoot"
                ref="timelineRoot"
                :aria-label="`Viewing Timeline: ${timelineName} (Press V to toggle on and off Simplified Form View, which may be easier for screen readers to navigate)`"
                class="timeline-root expand-horizontal" 
                :style="fullscreen ? 'position: absolute !important; top: 0px; bottom: 0px; left: 0px; right: 0px;background-color: #E7E3E0;' : 'height: calc(100% - 93px); position: relative; background-color: #E7E3E0;'" 
                :tabindex="0" 
                @wheel="wheel" 
                @mousedown="mouseDown"
                @mouseup="mouseUp"
                @mouseleave="mouseLeaveWatcher"
                @mousemove="dragWatcher" 
                @touchstart="touchStart" 
                @touchend="touchEnd"
                @touchmove="touchMove"
            >
                <div
                    role="heading"
                    aria-level="3"
                    aria-label="timeline"
                    style="position: absolute; width: 100%; height: 100%; pointer-events: none"
                />
                <!-- Grid lines go here -->
                <div
                    v-if="yearCount > 0"
                    class="year-container expand-width"
                >
                    <year-component
                        v-for="n in yearsToDraw"
                        :key="`${n}-${camInfo.yearToPixels}`"
                        aria-hidden="true"
                        :base-year="n"
                        :camera="camInfo"
                        :interval="yearInterval"
                        :root-height="rootBounds.height"
                        :year-count="yearCount"
                    />
                </div>
                <div
                    v-if="timelinenodes"
                    class="track-container expand-horizontal"
                >
                    <!-- Make the selected era on the bottom to allow overlapping eras to be selected -->
                    <!--<era-component v-if="selectedObject != null && selectedObject.isEra && selectedObject.isEra()" :node="selectedObject" :key="`era-${selectedObject.nodeid}`" :camera="camInfo" @regen-layout="refreshLayout" :rootHeight="rootBounds.height" :isCameraDragging="drag.isDragging" :eras="timelineeras"/>-->
                    <template v-for="node in sortedtimelinenodesfordisplay">
                        <era-component 
                            v-if="
                                node.isEra()
                                    && minYear <= node.dateEnd.getUTCFullYear()
                                    && maxYear >= node.dateStart.getUTCFullYear()
                            " 
                            :key="`era-${node.nodeid}`" 
                            :nodeid="node.nodeid" 
                            :camera="camInfo" 
                            :root-height="rootBounds.height" 
                            :is-camera-dragging="drag.isDragging" 
                            :eras="timelineeras" 
                            :tab-index="0"
                            @regen-layout="refreshLayout"
                            @scrolltonode="(n)=>{ scrollToNode(n, true); }"
                        />
                        <node-component
                            v-else-if="!(node.isEra()) && isOnScreen(node)"
                            :id="`node-${node.nodeid}`" 
                            ref="nodeComponent" 
                            :key="`node-${node.nodeid}`"
                            :circle-radius="nodeCircleRadius" 
                            :camera="camInfo" 
                            :track-cnt="tracks.length" 
                            :node="node" 
                            :root-height="rootBounds.height"
                            :tab-index="0"
                            @beginhover="(n)=>$set(drag, 'hoveredNode', n)" 
                            @endhover="(n)=>{ if (n == drag.hoveredNode) { $set(drag, 'hoveredNode', null) } }"
                            @regen-layout="refreshLayout"
                            @scrolltonode="(n)=>{ scrollToNode(n); }"
                        />
                    </template>

                    <svg
                        v-if="timelinenodes"
                        ref="connectionRoot"
                        class="connection-root"
                        :width="rootBounds.width"
                        :height="rootBounds.height"
                    >
                        <template v-for="conn in this.timelineconnections">
                            <connection-component
                                v-if="isConnectionOnScreen(conn)"
                                ref="connectionInst"
                                :key="`conn-${conn.nodeid1}-${conn.nodeid2}`"
                                :scale="connectionScale"
                                :connection="conn"
                                :camera="camInfo"
                                :parentBounds="rootBounds"
                            />
                        </template>
                        <!-- Does this janky one-element v-for so that the ref lists are joined -->
                        <template v-for="i in 1">
                            <connection-component
                                v-if="drag.activeNode && drag.isDragging"
                                ref="connectionInst"
                                :key="`active-${i}-conn-${drag.activeNode.nodeid}`"
                                :scale="connectionScale" 
                                :connection="dynamicConnection"
                                :overridePos="drag.mousePos"
                                :camera="camInfo"
                                :rootHeight="rootBounds.height"
                                :parentBounds="rootBounds"
                            />
                        </template>
                    </svg>
                    
                    <!-- <template v-for="node in timelineevents">
                        <node-component
                            v-if="isOnScreen(node)"
                            :id="`node-${node.nodeid}`" 
                            ref="nodeComponent" 
                            :key="`node-${node.nodeid}`"
                            :circle-radius="nodeCircleRadius" 
                            :camera="camInfo" 
                            :track-cnt="tracks.length" 
                            :node="node" 
                            :root-height="rootBounds.height"
                            :tab-index="0"
                            @beginhover="(n)=>$set(drag, 'hoveredNode', n)" 
                            @endhover="(n)=>{ if (n == drag.hoveredNode) { $set(drag, 'hoveredNode', null) } }"
                            @regen-layout="refreshLayout"
                            @scrolltonode="(n)=>{ scrollToNode(n, true); }"
                        />
                    </template> -->
                </div>
                <div v-else>
                    Loading...
                </div>
            </div>
            <div
                v-if="!embedded && !timelineFormMode"
                class="help-dropdown-btn"
                :style="`left: 5px; ${fullscreen ? 'top: 10px;' : 'top: 102px'}`"
            >
                <b-dropdown
                    v-b-tooltip="'help'"
                    no-caret
                    variant="Transparent"
                >
                    <template v-slot:button-content>
                        <i
                            class="fas fa-question-circle help-dropdown-btn-icon"
                            aria-label="help"
                        />
                    </template>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#build"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I start building my Timeline?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#connections"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I connect Events?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#upload"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I upload an image?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#onlineimages"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I add an online image to an Event?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#audio"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I add audio to an Event?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#presentationmode"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I use Presentation Mode?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#simplifiedview"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I access and use a Simplified View of my Timeline?
                    </b-dropdown-item>
                    <b-dropdown-item
                        href="https://cliovis.com/tutorials-and-instructions/#export"
                        target="_blank"
                    >
                        <i class="fas fa-external-link-alt" /> How do I export my Timeline as a text doc?
                    </b-dropdown-item>
                    <b-dropdown-item @click="$refs.supportModal.show()">
                        <i class="fas fa-ticket-alt" /> Do you need more help? <b>Request Support.</b>
                    </b-dropdown-item>
                </b-dropdown>
            </div>
            <b-btn
                v-if="timelineFormMode"
                v-b-tooltip
                title="exit form view"
                aria-label="exit form view"
                class="exit-form-btn"
                :style="`position: absolute; background-color: Transparent; color: #343B3F; border: none; font-size: 34px; ${fullscreen ? 'top: 2px;' : 'top: 95px;'}`"
                @click="toggleTimelineFormMode()"
            >
                <i class="fas fa-times" />
            </b-btn>
            <div
                v-else
                class="large-screens-only"
                :style="`position: absolute; right: 20px; z-index: 10; ${fullscreen ? 'top: 10px;' : 'top: 102px'}`"
            >
                <b-btn
                    ref="minimizeNavBtns"
                    v-b-tooltip="showTimelineNavBtns ? 'hide navigation buttons' : 'show navigation buttons'"
                    style="position: absolute; right: 2px; top; 5px; border-radius: 50%; width: 32px; height: 32px; padding: 3px 0 10px 5px;"
                    variant="cliovis-blue-dark"
                    :aria-label="showTimelineNavBtns ? 'hide timeline navigation buttons' : 'show timeline navigation buttons'"
                    @click="showTimelineNavBtns=!showTimelineNavBtns" 
                    @mouseleave="customBlur($refs.minimizeNavBtns)"
                >
                    <i :class="showTimelineNavBtns ? 'fas fa-eye-slash' : 'fas fa-eye'" />
                </b-btn>
                <div :class="showTimelineNavBtns ? 'full-scale' : 'zero-scale'">
                    <b-btn
                        ref="panLeftBtn"
                        aria-label="pan timeline left"
                        class="timeline-nav-btn"
                        style="right: 92px; top: 40px; width: 40px; height: 44px; font-size: 20px"
                        variant="white-transparent"
                        @mousedown="clickToPanLeft = true"
                        @mouseup="clickToPanLeft = false"
                        @mouseleave="clickToPanLeft = false, customBlur($refs.panLeftBtn)"
                        @keypress.enter="clickToPanLeft = true"
                        @keydown.enter="clickToPanLeft = false" 
                        @keyup.enter="clickToPanLeft = false"
                    >
                        <i class="fas fa-chevron-left" />
                    </b-btn>
                    <b-btn
                        ref="zoomInBtn"
                        aria-label="zoom in"
                        class="timeline-nav-btn"
                        style="right: 44px; top: 20px; width: 44px; height: 38px" 
                        variant="white-transparent"
                        @mousedown="clickToZoomIn = true"
                        @mouseup="clickToZoomIn = false"
                        @mouseleave="clickToZoomIn = false, customBlur($refs.zoomInBtn)"
                        @keypress.enter="clickToZoomIn = true"
                        @keydown.enter="clickToZoomIn = false" 
                        @keyup.enter="clickToZoomIn = false"
                    >
                        <i class="fas fa-plus" />
                    </b-btn>
                    <b-btn
                        ref="zoomOutBtn"
                        aria-label="zoom out"
                        class="timeline-nav-btn"
                        style="right: 44px; top: 62px; width: 44px; height: 38px" 
                        variant="white-transparent"
                        @mousedown="clickToZoomOut = true"
                        @mouseup="clickToZoomOut = false"
                        @mouseleave="clickToZoomOut = false, customBlur($refs.zoomOutBtn)"
                        @keypress.enter="clickToZoomOut = true"
                        @keydown.enter="clickToZoomOut = false" 
                        @keyup.enter="clickToZoomOut = false"
                    >
                        <i class="fas fa-minus" />
                    </b-btn>
                    <b-btn
                        ref="panRightBtn"
                        aria-label="pan timeline right"
                        class="timeline-nav-btn"
                        style="right: 0; top: 40px; width: 40px; height: 44px; font-size: 20px" 
                        variant="white-transparent"
                        @mousedown="clickToPanRight = true"
                        @mouseup="clickToPanRight = false"
                        @mouseleave="clickToPanRight = false, customBlur($refs.panRightBtn)"
                        @keypress.enter="clickToPanRight = true"
                        @keydown.enter="clickToPanRight = false" 
                        @keyup.enter="clickToPanRight = false"
                    >
                        <i class="fas fa-chevron-right" />
                    </b-btn>
                </div>
            </div>
            <div class="corner-buttons">
                <b-btn 
                    ref="searchBtn"
                    v-b-tooltip="'Search'" 
                    class="icon-button purple corner-button" 
                    :aria-label="'search for event or era'" 
                    @click.stop="()=>{isSearching = !isSearching;}"
                    @mouseleave="customBlur($refs.searchBtn)"
                >
                    <i 
                        class="fas fa-search"
                        aria-hidden="true"
                        style="text-align: center; font-size: 1.65rem; width: 100%; margin-top: 5%;"
                    />
                </b-btn>
                <b-btn 
                    ref="formViewBtn"
                    v-b-tooltip="timelineFormMode ? 'Visual Timeline View' : 'Simplified Form View'" 
                    class="icon-button purple corner-button" 
                    :aria-label="timelineFormMode ? 'switch to Visual Timeline View' : 'switch to Simplified Form View'" 
                    @click.stop="toggleTimelineFormMode()"
                    @mouseleave="customBlur($refs.formViewBtn)"
                >
                    <i 
                        v-if="!timelineFormMode" 
                        class="fas fa-th-list"
                        aria-hidden="true"
                        style="text-align: center; font-size: 1.65rem; width: 100%; margin-top: 5%;"
                    />
                    <i 
                        v-else
                        class="fas fa-sitemap"
                        aria-hidden="true"
                        style="text-align: center; font-size: 1.65rem; width: 100%; margin-top: 5%;"
                    />
                </b-btn>
                <b-btn
                    v-if="fullscreenEnabled"
                    ref="fullscreenBtn"
                    v-b-tooltip="isInBrowserFullScreen ? 'Exit Fullscreen' : 'Fullscreen'" 
                    class="icon-button purple corner-button" 
                    :aria-label="isInBrowserFullScreen ? 'Exit Fullscreen' : 'Fullscreen'" 
                    @click.stop="toggleBrowserFullScreen()"
                    @mouseleave="customBlur($refs.fullscreenBtn)"
                >
                    <img
                        v-if="isInBrowserFullScreen"
                        src="@/assets/icons/minimize.png"
                        aria-hidden="true"
                        style="width: 60%; height: auto;"
                    >
                    <img
                        v-else
                        src="@/assets/icons/full-screen-selector.png"
                        aria-hidden="true"
                        style="width: 60%; height: auto;"
                    >
                </b-btn>
            </div>
        </div>
        <b-modal
            ref="supportModal"
            ok-title="Send"
            ok-variant="cliovis-blue-light"
            centered
            title="Request Support"
            size="lg"
            @ok="supportModalSubmit"
            @hidden="modalKey++"
        >
            <support-form
                :key="modalKey"
                ref="supportForm"
                @close="$refs.supportModal.hide()"
            />
        </b-modal>
        <b-modal
            ref="subscriptionModal"
            centered
            title="Subscribe"
            ok-only
            ok-variant="secondary"
            ok-title="Close"
            size="wide-3"
            no-close-on-backdrop
            no-close-on-esc
        >
            <subscription-panel />
            <div
                v-if="checkAdmin() && !$store.state.project.project.upgradedOverride"
                style="margin-top: 15px; text-align: center; align-items: center"
            >
                <span><i
                    class="fas fa-certificate"
                    style="font-size: 24px; margin-right: 6px; color: #247a59;"
                />Or, would you like to remove the limit for yourself and all non-premium users of this Project?</span>
                <b-btn
                    v-b-tooltip.ds550="'Upgrade this Project if you want to collaborate with non-premium users & pay for their access to this Project'"
                    block
                    pill
                    variant="outline-dark"
                    style="max-width: 300px; margin: auto; margin-top: 10px"
                    @click="$refs.upgradeProjectModal.show()"
                >
                    <i class="fas fa-file-invoice-dollar" /> Upgrade Project
                </b-btn>
            </div>
        </b-modal>
        <b-modal
            ref="upgradeProjectModal"
            size="wide-3"
            :centered="true"
            title="Upgrade Project - Manage Payment Plan"
            :hide-footer="true"
            no-close-on-backdrop
            no-close-on-esc
        >
            <subscription-panel
                project
                :pid="$store.state.project.project.id"
                :on-subscribe="() => { this.$router.push(`/app/reconnect/${$store.state.project.project.id}/${timeline.id}`); }"
            />
        </b-modal>
    </div>
</template>

<script>
// npm
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ElementQueries from 'css-element-queries';
// eslint-disable-next-line no-unused-vars
import rgb from 'hsv-rgb';
// store/state/models
import KeyListener from '@/KeyListener';
import Mutations from '@/store/mutations';
// eslint-disable-next-line no-unused-vars
import UserGroup from '@/models/UserGroup';
// eslint-disable-next-line no-unused-vars
import User from '@/models/User';
// eslint-disable-next-line no-unused-vars
import Node from '@/models/Node';
import Connection, { ConnectionDirection } from '@/models/Connection';
import { PacketType } from '@/models/Packet';
import { SendPacket } from '@/networking/SendPacket';
// eslint-disable-next-line no-unused-vars
import Tag from '@/models/Tag';
import Vector2 from '@/models/Vector2';
// eslint-disable-next-line no-unused-vars
import Vector3 from '@/models/Vector3';
import Camera from '@/models/DOMCamera';
// eslint-disable-next-line no-unused-vars
import TimelineAccessUserData from '@/models/TimelineAccessUserData';
import TimelineLayout from './TimelineLayout';
// eslint-disable-next-line no-unused-vars
import ColorUtility from '@/util/ColorUtility';
import lerp from '@/util/Lerp';
// views/components
import TagUi from './TagUI.vue';
import NodeComponent from './TimelineViewerNode';
import EraComponent from './TimelineViewerEra';
import ConnectionComponent from './TimelineViewerConnection';
import YearComponent from './TimelineViewerYear';
import FullEventSlide from '@/components/presentation_mode/FullEventSlide.vue';
import BFormTextareaLimited from '@/components/input_limited/BFormTextareaLimited.vue';
import BestFitText from '@/components/BestFitText.vue';
import TimelineForm from './TimelineForm';
import Quicksearch from '@/components/Quicksearch.vue';
import { IsEmbedded } from '@/constants/environment';
import SupportForm from '@/components/support/SupportForm.vue';
// eslint-disable-next-line no-unused-vars
import { BvModalEvent } from 'bootstrap-vue';
import SubscriptionPanel from '@/components/payment/SubscriptionPanel.vue';
// import PaymentMethodManager from '@/components/payment/PaymentMethodManager.vue';
// eslint-disable-next-line no-unused-vars
import Project from '@/models/Project.ts';

function daysInMonth(month, year) 
{
    return new Date(year, month + 1, 0).getDate();
}

function dateTimeToFractionalYear(tgDateTimeObj)
{
    let tgYear = tgDateTimeObj.getUTCFullYear();
    let tgMonth = tgDateTimeObj.getUTCMonth();
    let tgDateDay = tgDateTimeObj.getUTCDate();
    let fracOffset = (tgMonth + tgDateDay / daysInMonth(tgMonth, tgYear)) / 12;
    let hoverDate = tgYear + fracOffset;
    return hoverDate;
}

export default Vue.extend({
    refs: ['timelineRoot'],
    components: 
    {
        NodeComponent,
        EraComponent,
        YearComponent,
        ConnectionComponent,
        TagUi,
        FullEventSlide,
        BFormTextareaLimited,
        BestFitText,
        TimelineForm,
        Quicksearch,
        SupportForm,
        SubscriptionPanel,
        // PaymentMethodManager,
    },
    props: 
    {
        sidePanelOpen: Boolean
    },
    data() 
    {
        return {
            fullscreenEnabled: true,
            isInBrowserFullScreen: document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement,
            browserFullScreenEvent: null,

            // This boolean corresponds to the old "fullscreen" mode, which simply expands the timeline.
            // Still functional, but not currently exposed as it has been replaced by proper full screen.
            fullscreen: false,
            deltaTime: 0.016,
            unsubscribeFromMutations: null,
            camInfo: new Camera(),
            drag: 
            {
                isMouseDown: false,
                isDragging: false,
                mouseX: 0,
                mouseY: 0,
                hoveredNode: null,
                activeNode: null,
                mousePos: new Vector2(0,0)
            },
            touches : null,
            resizeSensor : null,
            mouseDownNode : null,
            gridlines : new Map(),
            yearLabels : new Map(),
            camZoomFactor : 1,
            accumulatedZoomDelta: 0,
            shouldRegenLayout: false,
            destroyed: false,
            showDescriptionPopover: false,
            showCommentsPopover: false,
            editingName: false,
            // Value passed to nodes for reactivity; same as window.CircleRadius
            nodeCircleRadius: 0,
            isSearching: false,
            clickToNavTimelineInterval: false,
            showTimelineNavBtns: true,
            clickToPanLeft: false,
            clickToPanRight: false,
            clickToZoomIn: false,
            clickToZoomOut: false,
            needsSort: false,
            scrollNode: null,
            timelineRootTabIndex: 100,
            modalKey: 0
        };
    },
    watch: 
    {
        fullscreen()
        {
            console.log('Fullscreen modified');
            setTimeout(() =>
            {
                this.refreshLayout();
                this.$forceUpdate();
                this.$recompute('rootBounds');
            }, 0);
        },
        trackHeight(cur, old)
        {
            this.updateCSSVars(cur);
        },
        timelinelayoutevents(cur, old)
        {
            // TODO: always regen? is checking for changes just as expensive?
            let regen = false;
            if (cur.length !== old.length)
            {
                regen = true;
            }
            else
            {
                const test = new Set(old);
                cur.forEach((c) =>
                {
                    if (!(test.has(c)))
                    {
                        regen = true;
                    }
                });
            }

            if (regen)
            {
                // console.log('regen');
                this.shouldRegenLayout = true;
            }
        }
    },
    mounted()
    {
        this.fullscreenEnabled =
            document.fullscreenEnabled ||
            document.webkitFullscreenEnabled ||
            document.mozFullScreenEnabled ||
            document.msFullscreenEnabled;
        if (this.fullscreenEnabled)
        {
            this.browserFullScreenEvent = document.addEventListener('fullscreenchange', (event)=>
            {
                this.isInBrowserFullScreen = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
            });
        }
        this.unsubscribeFromMutations = this.$store.subscribe((mutation, state) =>
        {
            if (mutation.type == Mutations.setSelected)
            {
                let data = mutation.payload;
                if (data)
                {
                    if (data.isEra) // if it has that function, could be an Event
                    {
                        this.scrollToNode(data);
                    }
                    else if (data.nodeid1) // connection
                    {
                        // trick scrollToNode into thinking it's an Era (scroll to midpoint)
                        const dS1 = this.$store.state.project.nodes.get(data.nodeid1).dateStart;
                        const dS2 = this.$store.state.project.nodes.get(data.nodeid2).dateStart;
                        let dS = null;
                        let dE = null;
                        if (dS1.valueOf() < dS2.valueOf())
                        {
                            dS = dS1;
                            dE = dS2;
                        }
                        else
                        {
                            dS = dS2;
                            dE = dS1;
                        }
                        data.dateStart = dS;
                        data.dateEnd = dE;
                        data.isEra = () => true; // I think this won't cause weird duck typing issues in other spots? lol
                        // TODO: maybe we should do this more explicitly... I just didn't want to risk messing up the scroll code
                        
                        this.scrollToNode(data);
                    }
                }
            }
            else if (mutation.type == Mutations.setNode)
            {
                setTimeout(() =>
                {
                    this.refreshLayout();
                }, 0);
            }
            else if (mutation.type == Mutations.setConnection || mutation.type == Mutations.removeConnection)
            {
                //alert('Removing connections and recomputing scale');
                this.$recompute('timelineconnections');
                if (this.$refs.nodeComponent)
                {
                    for(let comp of this.$refs.nodeComponent)
                    {
                        comp.$recompute('circleScale');
                    }
                }
                setTimeout(() =>
                {
                    this.refreshLayout();
                    //alert('Done!');
                }, 0);
            }
            else if (mutation.type == Mutations.updateNodeStart)
            {
                this.needsSort = !this.needsSort;
            }
        });

        // Initial generation to create the tracks + node components
        TimelineLayout.generateLayout(this.timelinelayoutevents, this.camInfo);
        TimelineLayout.setYForAllTracks(this.rootBounds.height - 82);
        
        // Recompute the layout and invalidate everything
        setTimeout(() =>
        {
            this.wheel({deltaY: 0});
        }, 0);
        this.$nextTick(() =>
        {
            if (this.$refs.timelineRoot != null)
                this.$refs.timelineRoot.focus();

            if(this.end - this.start > 0)
            {
                const zoomMultiplier = 0.8;
                this.camInfo.yearToPixels = (1 / (this.endFloat - this.startFloat)) * 2000;
                this.camInfo.yearToPixels = Math.max(Math.min(this.camInfo.yearToPixels * zoomMultiplier, 60000), .125);
            }
            
            this.camInfo.minDate = this.averageStartYear - ((window.innerWidth / this.camInfo.yearToPixels) / 2);
            // Focus on the current year if no events are there yet
            if (this.timelineevents.length == 0)
            {
                this.camInfo.minDate = new Date().getFullYear() - this.yearCount / 2;
            } 
            
            //ElementQueries.ElementQueries.init();
            this.resizeSensor = new ElementQueries.ResizeSensor(this.$refs.rootNode, ()=>
            {
                this.$nextTick(()=>
                {
                    setTimeout(()=>
                    {
                        this.$recompute('rootBounds');
                        this.$recompute('connectionScale');
                        this.$forceUpdate();
                        this.camInfo.minDate++;
                        this.camInfo.minDate--;
                        setTimeout(() => 
                        {
                            this.$recompute('rootBounds');
                            this.$recompute('connectionScale');
                            //this.refreshLayout();
                            this.wheel({deltaY: 0});
                        }, 0);
                    }, 0);
                    this.updateCSSVars(this.trackHeight);
                });
            });

            let startTime = Date.now();
            KeyListener.init();
            KeyListener.reset();
            KeyListener.addKeyHandler(this);
            let loop = () =>
            {
                if (this.destroyed)
                    return;

                let curTime = Date.now();
                let delta = (curTime - startTime) / 1000;
                this.deltaTime = delta;
                const arrowSpeed = 10;
                const zoomSpeed = 2;
                startTime = curTime;
                try
                {
                    if (!this.shouldPreventArrows() && !(this.$store.state.project.presentationMode && this.$store.state.project.presentationFullSlide >= 0))
                    {
                        let deltaX = ((KeyListener.isArrowLeftDown()) ? -1 : (KeyListener.isArrowRightDown()) ? 1 : (this.clickToPanLeft) ? -1 : (this.clickToPanRight) ? 1 : 0) * arrowSpeed;
                        if (deltaX !== 0)
                        {
                            this.camInfo.minDate += deltaX * delta / this.camInfo.yearToPixels * 100;
                        }

                        let deltaY = ((KeyListener.isArrowUpDown()) ? 1 : (KeyListener.isArrowDownDown()) ? -1 : (this.clickToZoomOut) ? -1 : (this.clickToZoomIn) ? 1 : 0) * zoomSpeed;
                        if (deltaY !== 0)
                        {
                            let mult = (1 + deltaY * delta);
                            let rect = this.$refs.timelineRoot.getBoundingClientRect();
                            let relX = (rect.right - rect.left) / (2 * rect.width);
                            this.zoom(mult, relX);
                            this.refreshLayout();
                        }
                    }

                    if (this.shouldRegenLayout)
                    {
                        TimelineLayout.generateLayout(this.timelinelayoutevents, this.camInfo);
                        TimelineLayout.setYForAllTracks(this.rootBounds.height - 82);
                        this.$forceUpdate();
                        if (this.$refs.connectionInst)
                        {
                            for(let conn of this.$refs.connectionInst)
                            {
                                if (conn.manageTransition)
                                {
                                    conn.manageTransition();
                                }
                            }
                        }

                        if (this.$refs.nodeComponent != null)
                        {
                            for (let comp of this.$refs.nodeComponent)
                            {
                                comp.$recompute('bound');
                                comp.$recompute('yPos');
                            }
                        }
                        this.shouldRegenLayout = false;
                        this.updateCSSVars(this.trackHeight);
                    }
                }
                catch(ex)
                {
                    console.error(ex);
                }
                requestAnimationFrame(loop);
            };
            requestAnimationFrame(loop);
        });
    },
    beforeDestroy()
    {
        if (this.fullscreenEnabled)
        {
            if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
            {
                document.exitFullscreen();
            }

            if (this.browserFullScreenEvent)
            {
                document.removeEventListener('fullscreenchange', this.browserFullScreenEvent);
            }
        }
        KeyListener.removeKeyHandler(this);
        if (this.unsubscribeFromMutations) this.unsubscribeFromMutations();
        this.unsubscribeFromMutations = null;
        this.destroyed = true;
    },
    recomputed: 
    {
        timelineconnections()
        {
            let tl = this.timeline;
            if (tl != null)
            {
                var connections = [];
                for (const key of this.$store.getters.connections.keys())
                {
                    if (tl.nodeIDs.includes(key))
                    {
                        connections = connections.concat([...this.$store.getters.connections.get(key).values()]); // NOTE: just assuming that node2 is in the timeline for now
                    }
                }
                //console.log(connections);
                return connections;
            }
            return null;
        },
        dynamicConnection()
        {
            let conn = new Connection;
            conn.nodeid1 = this.drag.activeNode.nodeid;
            conn.nodeid2 = -1;
            if (this.drag.hoveredNode && this.drag.hoveredNode != this.drag.activeNode)
            {
                conn.nodeid2 = this.drag.hoveredNode.nodeid;
            }
            conn.description = '';
            conn.references = '';
            conn.direction = ConnectionDirection.Forward;
            return conn;
        },
        rootBounds()
        {
            if (!this.$refs.timelineRoot)
            {
                return {x: 0, y: 0, width: 0, height: 0, top: 0, right: 0, bottom: 0, left: 0};
            }
            let size = this.$refs.timelineRoot.getBoundingClientRect();
            return size;
        },
        connectionScale() 
        {
            let size = window.innerWidth * Math.min(this.trackHeight, 32) / 64;
            
            // 2560x1440 is the reference resolution I was working on this at
            //return Math.clamp(1.5 * Math.min(size / 2560, 1), .3, 2.25);
            let x = Math.max(1.5 * Math.min(size / 2560, 1), .3);
            return Math.min(x, 2.25);
        },
    },
    computed:
    {
        ...mapGetters([
            'checkTimelineAdmin',
            'checkTimelineWrite',
            'checkProjectModerate',
            'timelineFormMode',
            'checkAdmin'
        ]),
        yearsToDraw()
        {
            // This is an inefficient way of doing this, but meh
            let res = [];
            let start = this.minYear - this.minYear % this.yearInterval;
            let end = this.maxYear + this.yearInterval;
            for(let i = start; i < end; i += this.yearInterval)
            {
                res.push(i);
            }
            return res;
        },
        yearInterval()
        {
            // Really wish I'd left a comment for what the 180 magic number is for,
            // because now it's been 3 years and I have no idea.
            let base = Math.max(Math.floor(180 / this.camInfo.yearToPixels), 1);
            if (base == 1)
            {
                return 1;
            }

            const baseNumberForLog = base * 1.1;
            const log = Math.log10(baseNumberForLog);
            const flooredLog = Math.round(log);
            if (flooredLog > 0)
            {
                /*
                const floor = log;
                const remainder = log - floor;
                let add = 0;
                if (remainder > 0.5)
                    add += floor / 2;

                console.log(`${remainder}, ${floor}, ${add}`);
                */
                const doubleFloor = Math.floor(log * 2);
                const steppedFloor = doubleFloor / 2;
                const intermediate = Math.pow(10, flooredLog);
                const final = intermediate + (steppedFloor - flooredLog) * intermediate;
                // console.log(`${log}, ${steppedFloor}, ${intermediate}, ${final}`);
                return final;
            }
            
            let base2 = base + base % 2;
            let base5 = base + base % 5;
            if (Math.abs(base5 - base) < Math.abs(base2 - base))
            {
                return base5;
            }
            return base2;
        },
        fractionalYearCount()
        {
            if (!this.$refs.timelineRoot)
            {
                return 0;
            }
            
            this.camInfo.minDate;
            let size = this.$refs.timelineRoot.clientWidth;
            return size / this.camInfo.yearToPixels;
        },
        yearCount()
        {
            if (!this.$refs.timelineRoot)
            {
                return 0;
            }
            
            this.camInfo.minDate;
            let size = this.$refs.timelineRoot.clientWidth;
            return Math.ceil(size / this.camInfo.yearToPixels);
        },
        minYear()
        {
            return Math.floor(this.camInfo.minDate);
        },
        maxYear()
        {
            return this.minYear + this.yearCount;
        },
        watermarkImage()
        {
            const value = this.$store.state.project.project.watermarkImage;
            return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '';
        },
        watermarkLink()
        {
            const value = this.$store.state.project.project.watermarkLink;
            return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '';
        },
        tracks()
        {
            return TimelineLayout.trackList;
        },
        trackHeight()
        {
            // console.log('trackHeight');
            // console.log(TimelineLayout.maxAllocatedTrackHeight);
            // console.log(this.rootBounds.height);
            // console.log(this.tracks.length);
            if (this.camInfo.yearToPixels && this.camInfo.minDate)
            {}
            return Math.min(TimelineLayout.maxAllocatedTrackHeight, this.rootBounds.height / this.tracks.length);
        },
        isAnythingSelected()
        {
            return this.selectedObject != null;
        },
        selectedObject()
        {
            return this.$store.state.project.selected;
        },
        timeline()
        {
            return this.$store.state.project.activeTimeline;
        },
        nodes()
        {
            return this.$store.getters.nodes;
        },
        timelinenodes()
        {
            let tl = this.timeline;
            if (tl != null)
            {
                var tlnodes = [];
                for (const nodeid of tl.nodeIDs)
                {
                    //console.log(nodeid);
                    tlnodes.push(this.nodes.get(nodeid));
                }
                return tlnodes;
            }
            return null;
        },
        sortedtimelinenodes()
        {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            let sorted = (this.timelinenodes == null) ? null : (this.timelinenodes.sort((a, b) =>
            {
                return a.dateStart.valueOf() - b.dateStart.valueOf();
            }));
            return this.needsSort ? sorted : sorted;
        },
        sortedtimelinenodesfordisplay()
        {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            let sorted = (this.timelinenodes == null) ? null : (this.timelinenodes.sort((a,b)=>
            {
                // if A is contained in B, it should be on top
                if (a.dateStart >= b.dateStart && a.dateEnd < b.dateEnd)
                {
                    return 1;
                }
                // if B is contained in A, it should be on top
                else if (b.dateStart >= a.dateStart && b.dateEnd < a.dateEnd)
                {
                    return -1;
                }
                else if (a.dateStart == b.dateStart)
                {
                    return a.dateEnd - b.dateEnd;
                }
                return a.dateStart - b.dateStart;
            }));
            return this.needsSort ? sorted : sorted;
        },
        timelineevents()
        {
            let tl = this.timeline;
            if (tl != null)
            {
                var tlnodes = [];
                for (const nodeid of tl.nodeIDs)
                {
                    //console.log(nodeid);
                    let n = this.nodes.get(nodeid);
                    if (n.isEvent())
                    {
                        tlnodes.push(n);
                    }
                }
                return tlnodes;
            }
            return null;
        },
        timelinelayoutevents()
        {
            if (this.timelineevents != null)
            {
                const min = this.camInfo.minDate;
                const max = this.camInfo.minDate + window.innerWidth / this.camInfo.yearToPixels;
                // console.log(`only use nodes in range ${min} - ${max} and relatives`);
                // const layoutEvents = new Set();
                const inViewEvents = [];
                this.timelineevents.forEach((node) =>
                {
                    const start = dateTimeToFractionalYear(node.dateStart);
                    // let end = dateTimeToFractionalYear(node.dateEnd);
                    // const tailLength = (end - start) * this.camInfo.yearToPixels;
                    // const nameLengthEstimate = node.name.length * this.trackHeight * (2 / 5);
                    // if (tailLength < nameLengthEstimate)
                    // {
                    //     end += (nameLengthEstimate - tailLength) / this.camInfo.yearToPixels;
                    // }
                    const end = dateTimeToFractionalYear(node.dateEnd) + (400 / this.camInfo.yearToPixels);
                    if (!(start > max || end < min))
                    {
                        // layoutEvents.add(node);
                        inViewEvents.push(node);
                    }
                });
                // inViewEvents.forEach((node) =>
                // {
                //     node.forAllRelated((r) =>
                //     {
                //         layoutEvents.add(r);
                //     });
                // });
                // return [...layoutEvents];
                return inViewEvents;
            }
            return null;
        },
        timelineeras()
        {
            let tl = this.timeline;
            if (tl != null)
            {
                var tlnodes = [];
                for (const nodeid of tl.nodeIDs)
                {
                    //console.log(nodeid);
                    /** @type {Node} */
                    let n = this.nodes.get(nodeid);
                    if (n.isEra())
                    {
                        tlnodes.push(n);
                    }
                }
                tlnodes.sort((a,b)=>
                {
                    // if A is contained in B, it should be on top
                    if (a.dateStart >= b.dateStart && a.dateEnd < b.dateEnd)
                    {
                        return 1;
                    }
                    // if B is contained in A, it should be on top
                    else if (b.dateStart >= a.dateStart && b.dateEnd < a.dateEnd)
                    {
                        return -1;
                    }
                    else if (a.dateStart == b.dateStart)
                    {
                        return a.dateEnd - b.dateEnd;
                    }
                    return a.dateStart - b.dateStart;
                });
                return tlnodes;
            }
            return null;
        },
        connections()
        {
            return this.$store.getters.connections;
        },
        timelineName()
        {
            return this.timeline != null ? this.timeline.name : '[null]';
        },
        start()
        {
            if (this.timelinenodes.length === 0)
            {
                return 0;
            }
            let minYear = Number.MAX_SAFE_INTEGER;
            for (const n of this.timelineevents)
            {
                let year = n.dateStart.getUTCFullYear();
                if (minYear > year)
                {
                    minYear = year;
                }
            }
            return minYear;
        },
        startFloat()
        {
            if (this.timelinenodes.length === 0)
            {
                return 0;
            }
            let minYear = Number.MAX_SAFE_INTEGER;
            for (const n of this.timelinenodes)
            {
                let year = dateTimeToFractionalYear(n.dateStart);
                if (minYear > year)
                {
                    minYear = year;
                }
            }
            return minYear;
        },
        end()
        {
            if (this.timelinenodes.length === 0)
            {
                return 0;
            }
            let maxYear = Number.MIN_SAFE_INTEGER;
            for (const n of this.timelineevents)
            {
                let year = n.dateEnd.getUTCFullYear();
                if (maxYear < year)
                {
                    maxYear = year;
                }
            }
            return maxYear;
        },
        endFloat()
        {
            if (this.timelinenodes.length === 0)
            {
                return 0;
            }
            let maxYear = Number.MIN_SAFE_INTEGER;
            for (const n of this.timelinenodes)
            {
                let year = dateTimeToFractionalYear(n.dateEnd);
                if (maxYear < year)
                {
                    maxYear = year;
                }
            }
            return maxYear;
        },
        averageStartYear()
        {
            if (this.timelinenodes.length === 0)
            {
                return 0;
            }
            /*
            let totalYear = 0;
            for (const n of this.timelineevents)
            {
                totalYear += dateTimeToFractionalYear(n.dateStart);
            }
            return totalYear / this.timelineevents.length;
            */
            return (this.endFloat + this.startFloat) / 2;
        },
        embedded()
        {
            return IsEmbedded || this.$route.fullPath.includes('app/e/');
        },
        editName: 
        {
            get() 
            {
                return this.timeline.name;
            },
            set(value) 
            {
                this.timeline.name = value;
                SendPacket(PacketType.TimelineUpdateTitle, {timelineid: this.timeline.id, value: this.timeline.name});
            }
        },
        editDescription: 
        {
            get() 
            {
                return this.timeline.description;
            },
            set(value) 
            {
                this.timeline.description = value;
                SendPacket(PacketType.TimelineUpdateDescription, {timelineid: this.timeline.id, value: this.timeline.description});
            }
        },
        editComments: 
        {
            get() 
            {
                return this.timeline.comments;
            },
            set(value) 
            {
                this.timeline.comments = value;
                SendPacket(PacketType.TimelineUpdateComments, {timelineid: this.timeline.id, value: this.timeline.comments});
            }
        }
    },
    methods:
    {
        toggleBrowserFullScreen()
        {
            const elem = document.documentElement;
            if (elem.requestFullscreen)
            {
                if (document.fullscreenElement)
                {
                    document.exitFullscreen();
                } 
                else
                {
                    elem.requestFullscreen();
                }
                this.isInBrowserFullScreen = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
            }
        },
        isOnScreen(node)
        {
            const min = this.camInfo.minDate;
            const max = this.camInfo.minDate + window.innerWidth / this.camInfo.yearToPixels;
            const start = dateTimeToFractionalYear(node.dateStart) - (100 / this.camInfo.yearToPixels);
            // let end = dateTimeToFractionalYear(node.dateEnd);
            // const tailLength = (end - start) * this.camInfo.yearToPixels;
            // const nameLengthEstimate = node.name.length * this.trackHeight * (2 / 5);
            // if (tailLength < nameLengthEstimate)
            // {
            //     end += (nameLengthEstimate - tailLength) / this.camInfo.yearToPixels;
            // }
            const end = dateTimeToFractionalYear(node.dateEnd) + (400 / this.camInfo.yearToPixels);
            return !(start > max || end < min);
        },
        isConnectionOnScreen(conn)
        {
            const node1 = this.$store.getters.nodes.get(conn.nodeid1);
            const node2 = this.$store.getters.nodes.get(conn.nodeid2);
            if (!node1 || !node2 || (!(node1.component) && !(node2.component)))
            {
                return false;
            }
            const min = this.camInfo.minDate;
            const max = this.camInfo.minDate + window.innerWidth / this.camInfo.yearToPixels;
            const start1 = dateTimeToFractionalYear(node1.dateStart) - (100 / this.camInfo.yearToPixels);
            const end1 = dateTimeToFractionalYear(node1.dateEnd) + (400 / this.camInfo.yearToPixels);
            const start2 = dateTimeToFractionalYear(node2.dateStart) - (100 / this.camInfo.yearToPixels);
            const end2 = dateTimeToFractionalYear(node2.dateEnd) + (400 / this.camInfo.yearToPixels);
            return !(start1 > max || end1 < min) // node1 on screen
                || !(start2 > max || end2 < min); // node2 on screen
            // || (start1 > max && end2 < min) // node1 offscreen right and node2 offscreen left
            // || (start2 > max && end1 < min); // node2 offscreen right and node1 offscreen left
        },
        supportModalSubmit(modal)
        {
            modal.preventDefault();
            this.$nextTick(()=>
            {
                this.$refs.supportForm.submit();
            });
        },
        activeElement()
        {
            return document.activeElement;
        },
        keyHandler(event, isTextBoxFocused)
        {
            if (!isTextBoxFocused)
            {
                if (KeyListener.isVDown())
                {
                    this.toggleTimelineFormMode();
                }

                if (KeyListener.isSDown())
                {
                    event.preventDefault();
                    this.isSearching = !this.isSearching;
                }
            }
            
            if (KeyListener.isEscDown())
            {
                this.isSearching = false;
                // TODO: other escapable things?
            }
        },
        toggleTimelineFormMode()
        {
            this.$store.commit(Mutations.setTimelineFormMode, !(this.timelineFormMode));
        },
        openQuicksearch()
        {
            this.isSearching = true;
        },
        shouldPreventArrows()
        {
            /** @type {HTMLElement | undefined} */
            let active = this.activeElement();
            
            if (!active) return false;
            
            let lowerName = active.tagName.toLowerCase();
            // console.log(`<${lowerName}>`);
            
            if (
                lowerName === 'input'
                || 	lowerName === 'textarea'
                ||	lowerName === 'select'
            ) return true;

            return false;
        },
        scrollToNode(node, forceIfNotSelected = false)
        {
            this.scrollNode = node;
            // const startTime = new Date();

            let lerpLoop = () =>
            {
                // console.log(node.name);
                // console.log(this.scrollNode ? this.scrollNode.name : 'no scroll node');
                if (this.destroyed 
                    || this.scrollNode != node
                    || (this.$store.state.project.selected != node && !forceIfNotSelected)
                    || this.drag.isMouseDown
                    || (!this.shouldPreventArrows()
                        && (KeyListener.isArrowLeftDown() || KeyListener.isArrowRightDown()))
                )
                {
                    return;
                }

                // TODO: improve the interaction of the sidebar open/close transition on the scrollToNode lerp
                // const now = new Date();
                // const elapsed = now.valueOf() - startTime.valueOf();
                // if (this.$store.state.project.selected === node && elapsed < 50)
                // {
                //     requestAnimationFrame(lerpLoop);
                //     return;
                // }

                let tgDateTimeObj = node.dateStart;
                if (node.isEra())
                {
                    // Done this way for precision...ish.  Not sure how much of a difference it makes since
                    // they're both floating point numbers when they move into js land.
                    let time = (node.dateStart.getTime() / 2 + node.dateEnd.getTime() / 2);
                    tgDateTimeObj = new Date(time);
                }
                let hoverDate = dateTimeToFractionalYear(tgDateTimeObj);
                let tgDate = hoverDate - (this.fractionalYearCount) / 2;
                const t = 1 - Math.exp(-7 * this.deltaTime);
                this.camInfo.minDate = lerp(
                    this.camInfo.minDate,
                    tgDate,
                    t
                );
                if (Math.abs(this.camInfo.minDate - tgDate) > (1 / this.camInfo.yearToPixels))
                {
                    requestAnimationFrame(lerpLoop);
                }
                else
                {   
                    this.scrollNode = null;
                }
            };
            requestAnimationFrame(lerpLoop);
        },
        updateCSSVars(trackHeightValue)
        {
            // console.log('******************');
            // console.log(this.camInfo.minDate);
            // console.log(this.camInfo.yearToPixels);
            // console.log(trackHeightValue);
            // This makes it so that by default, nodes are half the height of the track
            let radius = trackHeightValue / 2;
            this.nodeCircleRadius = radius;
            let root = document.documentElement;

            // bounds for yearToPixels == [2, 500]
            // let fontSize = lerp(16, 24, (this.camInfo.yearToPixels - 2) / 498) * Math.min(1, window.screen.height * window.devicePixelRatio / 1080);
            
            // if (trackHeightValue < 19)
            // {
            // }
            
            // let fontSize = Math.min(24, trackHeightValue * (13 / 20) * Math.min(1, window.screen.height * window.devicePixelRatio / 1080));
            
            let fontSize = Math.min(20, trackHeightValue * (13 / 40) * Math.min(1, window.innerHeight * window.devicePixelRatio / 1080));

            // console.log(trackHeightValue);
            // console.log(this.nodeCircleRadius);
            // console.log(fontSize);

            /*
            // Defaults
            --circle-radius: 64px;
            --circle-x-offset: -32px;
            --circle-y-offset: -16px;
            --line-radius: 32px;
            --node-font-sizeL 30px;
            */
            window.CircleRadius = radius;
            root.style.setProperty('--circle-radius', `${radius}px`);
            root.style.setProperty('--circle-x-offset', `${-radius / 2}px`);
            root.style.setProperty('--circle-y-offset', `${-radius / 3}px`);
            root.style.setProperty('--line-radius', `${radius / 3}px`);
            root.style.setProperty('--node-font-size', `${fontSize}px`);
            root.style.setProperty('--inv-node-font-size', `-${fontSize / 2}px`);
        },
        touchStart(ev)
        {
            //console.log('touchStart');
            //ev.preventDefault();
            this.touches = {};
            Object.assign(this.touches, ev.targetTouches);
            if (ev.targetTouches.length === 1)
            {
                // treat single finger touch events on the timeline viewer as mouse presses
                let fakeMouseEvent = ev.targetTouches.item(0);
                fakeMouseEvent.button = 0;
                fakeMouseEvent.preventDefault = () => 
                {};
                this.mouseDown(fakeMouseEvent);
            }
        },
        touchEnd(ev)
        {
            //console.log('touchEnd');
            //ev.preventDefault();
            if (ev.changedTouches.length === 1)
            {
                // treat single finger touch events on the timeline viewer as mouse presses
                let fakeMouseEvent = ev.changedTouches.item(0);
                fakeMouseEvent.button = 0;
                fakeMouseEvent.preventDefault = () => 
                {};
                this.mouseUp(fakeMouseEvent);
                setTimeout(() =>
                {
                    if (this.drag.hoveredNode != null)
                    {
                        this.$set(this.drag, 'hoveredNode', null);
                    }
                }, 0);
            }
        },
        touchMove(ev)
        {
            //console.log('touchMove');
            ev.preventDefault();
            if (ev.targetTouches.length === 1)
            {
                // treat single finger touch events on the timeline viewer as mouse presses
                let fakeMouseEvent = ev.targetTouches.item(0);
                fakeMouseEvent.button = 0;
                fakeMouseEvent.preventDefault = () => 
                {};

                if (this.drag.activeNode != null)
                {
                    // you're dragging from a node but 
                    // dynamicConnection will fail b/c (this.drag.hoveredNode == null || this.drag.hoveredNode == this.drag.activeNode) when using touch
                    // so here is a really not great fix (but I can't think of any better approach rn)...
                    // TODO: this fix is really bad and should be replaced with any alternative if at all possible
                    let element = document.elementFromPoint(fakeMouseEvent.clientX, fakeMouseEvent.clientY);		
                    if (element != null && element.id != null && element.id.indexOf('node') != -1)
                    {
                        let nodeid = parseInt(element.id.replace(/^\D+/g, ""));
                        let node = this.nodes.get(nodeid);
                        this.$set(this.drag, 'hoveredNode', node);
                    }
                    else
                    {
                        this.$set(this.drag, 'hoveredNode', null);
                    }
                }

                this.dragWatcher(fakeMouseEvent);
            }
            else if (ev.targetTouches.length === 2)
            {
                // pinch to zoom
                let touchStart1 = this.touches[0];
                let touchStart2 = this.touches[1];
                let touchNow1 = ev.targetTouches.item(0);
                let touchNow2 = ev.targetTouches.item(1);

                let startMidX = (touchStart2.clientX + touchStart1.clientX) / 2;

                let startXDist = touchStart2.clientX - touchStart1.clientX;
                let startYDist = touchStart2.clientY - touchStart1.clientY;
                let startSqrDist = (startYDist * startYDist) + (startXDist * startXDist);

                let nowMidX = (touchNow2.clientX + touchNow1.clientX) / 2;

                let nowXDist = touchNow2.clientX - touchNow1.clientX;
                let nowYDist = touchNow2.clientY - touchNow1.clientY;
                let nowSqrDist = (nowYDist * nowYDist) + (nowXDist * nowXDist);

                let midMidX = (nowMidX + startMidX) / 2;
                let sqrDistDelta = nowSqrDist - startSqrDist;

                let fakeWheelEvent = 
                {
                    clientX: midMidX,
                    deltaY: -sqrDistDelta,
                    preventDefault: () => 
                    {},
                    scale: 0.25
                };

                this.touches[0] = touchNow1;
                this.touches[1] = touchNow2;

                //console.log(fakeWheelEvent);

                this.wheel(fakeWheelEvent);
            }
        },
        getMouseHoveredYear(ev)
        {
            let cx = ev.clientX;
            let rect = this.$refs.timelineRoot.getBoundingClientRect();
            let relX = (cx - rect.left) / rect.width;
            
            let min = this.minYear;
            let max = this.maxYear;
            let hoveredYear = relX * (max - min) + min;
            return hoveredYear;
        },
        mouseDown(ev)
        {
            if (ev.button !== 0)
                return;

            ev.preventDefault();

            //this.$refs.timelineRoot.focus();

            KeyListener.reset();

            this.drag.isMouseDown = true;
            this.drag.mouseX = ev.clientX;
            this.drag.mouseY = ev.clientY;

            if (this.drag.activeNode !== this.drag.hoveredNode 
                && this.checkTimelineWrite() && !(this.$store.state.project.presentationMode))
            {
                this.$set(this.drag, 'activeNode', this.drag.hoveredNode);
            }
        },
        mouseUp(ev)
        {
            if (ev.button !== 0)
                return;

            this.drag.isMouseDown = false;
            if (this.drag.isDragging)
            {
                if (this.drag.activeNode)
                {
                    // Connection drag
                    // nodeid2 == -1 if there is no second node
                    if (this.dynamicConnection.nodeid2 != -1)
                    {
                        this.$store.commit(Mutations.setConnection, this.dynamicConnection);
                        SendPacket(PacketType.NodeAddConnection, 
                            {
                                nodeid: this.dynamicConnection.nodeid1,
                                otherid: this.dynamicConnection.nodeid2
                            });

                        this.$store.commit(Mutations.setSelected, this.dynamicConnection);
                        this.$emit('edit-selected', 'connection');
                        //console.log(`CREATE CONNECTION FROM ${this.drag.activeNode.nodeid} TO ${this.drag.hoveredNode.nodeid}`);
                    }
                    this.$set(this.drag, 'activeNode', null);
                    this.drag.isDragging = false;
                }
                else
                {
                    // Timeline drag
                    setTimeout(() =>
                    {
                        this.drag.isDragging = false;
                    }, 0);
                }
            }
            else
            {
                if (this.drag.activeNode)
                {
                    this.$set(this.drag, 'activeNode', null);
                }
                this.$emit('close-side-panel');
            }
        },
        mouseLeaveWatcher(ev)
        {
            if (this.drag.isMouseDown)
            {
                this.drag.isMouseDown = false;
                if (this.drag.isDragging)
                {
                    setTimeout(() =>
                    {
                        this.drag.isDragging = false;
                        console.log('Removing active node');
                        this.$set(this.drag, 'activeNode', null);
                    }, 0);
                }
            }
        },
        dragWatcher(ev)
        {
            this.drag.mousePos.x = ev.clientX - this.rootBounds.left;
            this.drag.mousePos.y = ev.clientY - this.rootBounds.top;
            if (this.drag.isMouseDown)
            {
                if (this.drag.isDragging)
                {
                    const delta = this.drag.mouseX - ev.clientX;
                    if (this.drag.activeNode)
                    {
                        // TODO: Set up active node
                    }
                    else
                    {
                        if (delta != 0)
                        {
                            //this.refreshLayout();
                        }
                        this.camInfo.minDate += delta / this.camInfo.yearToPixels;
                    }
                    this.drag.mouseX = ev.clientX;
                    this.drag.mouseY = ev.clientY;
                }
                else
                {
                    let dx = ev.clientX - this.drag.mouseX;
                    let dy = ev.clientY - this.drag.mouseY;
                    if (Math.sqrt((dx * dx) + (dy * dy)) > 5)
                    {
                        console.log(`Starting drag with node ${this.drag.hoveredNode ? this.drag.hoveredNode.name : '[NONE]'}`);
                        this.drag.isDragging = true;
                    }
                }
            }
        },
        refreshLayout() 
        {
            this.shouldRegenLayout=true;
        },
        zoom(mult, x)
        {
            let oldMin = this.camInfo.minDate;
            let oldMax = this.camInfo.minDate + this.fractionalYearCount;
            let oldHoveredYear = x * (oldMax - oldMin) + oldMin;
            //console.log(oldHoveredYear);
            this.camInfo.yearToPixels *= mult;
            this.camInfo.yearToPixels = Math.max(Math.min(this.camInfo.yearToPixels, 60000), .125);
            //console.log(this.camInfo.yearToPixels);

            //this.$nextTick(()=>{
            let newMin = this.camInfo.minDate;
            let newMax = this.camInfo.minDate + this.fractionalYearCount;
            let newHoveredYear = x * (newMax - newMin) + newMin;
            this.camInfo.minDate -= newHoveredYear - oldHoveredYear;
            //});
        },
        wheel(ev)
        {
            if (ev.deltaY != 0  && !(this.$store.state.project.presentationMode && this.$store.state.project.presentationFullSlide >= 0))
            {
                ev.preventDefault();

                KeyListener.reset();

                let mult = ev.scale ? ev.scale : 1;
                if (Math.sign(ev.deltaY) < 0)
                {
                    mult = 1 + (mult) / 5;
                }
                else
                {
                    mult = 1 - (mult) / 5;
                }
                
                let cx = ev.clientX;
                let rect = this.$refs.timelineRoot.getBoundingClientRect();
                let relX = (cx - rect.left) / rect.width;
                
                this.zoom(mult, relX);

                this.updateCSSVars(this.trackHeight);
            }
            
            this.refreshLayout();
        },
        customBlur(el)
        {
            el.blur();
        },
        centerYear()
        {
            return Math.floor((this.minYear + this.maxYear) / 2.0);
        }
    }
});
</script>

<style lang="scss" scoped>
.timeline-root
{
    overflow-x: hidden;
    overflow-y: hidden;
}

.expand-horizontal
{
    width: 100%;
}

.viewer-root
{
    position: fixed;
    overflow-x: hidden;
    overflow-y: hidden;
    top: 45px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    padding: 0px;
    max-height: 100%;
    max-width: 100%;
}

.center-text
{
    text-align: center;
}

.track
{
    width: 100%;
    min-height: 50px;
    max-height: 100px;
    //outline-style: dotted;
}

.track-container
{
    position: absolute;
}

.connection-root
{
    position: absolute;
    z-index: 1;
    
    // Block the background from receiving events
    pointer-events: none;
}

.connection-root *
{
    // Allow children to receive events
    pointer-events: auto;
}

.icon-button
{
    margin: 3px; 
    height: 32px; 
    width: 32px;
    padding: 5px;
    border-radius: 100%;
    text-align: center;
    vertical-align: top;
    position: relative;
}

.purple
{
    background-color: #24457a;
}

#body 
{
    min-height: 100%;
    min-width: 100%;
    max-height: 100%;
    max-width: 100%;
}

.project-watermark
{
    display: flex;
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
    margin: 5px;

    img
    {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.has-project-watermark .timelineTitleWidth
{
    flex: 1;
    min-width: 0;
}

.timelineTitleWidth
{
    width: 60%;

    @media (max-width: 768px)
    {
        width: 68%;
    }
}

.tagUI 
{
    width: 36%;

    @media (max-width: 768px)
    {
        width: 24%;
    }

    min-width: 110px;
}

.corner-buttons
{
    position: absolute; 
    right: 15px; 
    bottom: 15px; 
    z-index: 10;

    @media (max-width: 400px) {
        bottom: 30px;
    }

    @media (max-height: 400px) {
        bottom: 5px;
    }
}

.corner-button
{
    width: 3.75vw;
    height: 3.75vw;
    min-width: 48px;
    min-height: 48px;
    max-width: 512px;
    max-height: 512px;
    margin-right: 10px;
}

.quicksearch 
{
    margin: auto;
    position: absolute;
    top: calc(50% - 50px);
    left: 0;
    right: 0;
    width: 40vw;
    min-width: calc(min(500px, 95vw));
    max-width: calc(min(1024px, 40vw));
    height: 100px;
    z-index: 10;
}

.timeline-nav-btn
{
    transition: 300ms;
    position: absolute;
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.15);
    color: #4d535e;
    font-size: 18px;
}

.timeline-nav-btn:hover
{
    transition: 200ms;
    transform: scale(1.05);
    background-color: #fff;
}

.timeline-nav-btn:focus
{
    transition: 200ms;
    transform: scale(1.05);
    background-color: rgba(255, 255, 255, 0.9) !important;
    box-shadow: 0 0 .5pt 1.75pt #427ad6 !important;
}

.exit-form-btn
{
    transition: 300ms;
    background-color: Transparent; 
    color: #343B3F; 
    border: none;
    right: 20px;

    @media (max-width: 800px)
    {
        @media only screen and (orientation:portrait){
            right: -5px;
        }

        @media only screen and (orientation:landscape){
            right: 5px;
        }
    }
}

.exit-form-btn:hover
{
    transition: 200ms;
    //transform: scale(1.05);
    color: #4d535e !important;
}

.full-scale
{
    transition: 300ms;
    transform: scale(1);
}

.zero-scale
{
    transition: 200ms;
    transform: scale(0);
}
</style>